import { Controller } from '@hotwired/stimulus';

// The location step's question tree. The work model reveals either the office branch or the
// remote branch, and under Remote the worldwide and timezone answers reveal the rest. Every
// block's visibility is derived afresh from the current answers on each change, so nothing
// depends on the order the answers came in. The employer edit form shares it: its answers are
// radios where the wizard's are selects, and an answer is read from whichever the block holds.
export default class extends Controller {
  static targets = [
    'workModel', 'office', 'attendance', 'attendanceCount', 'worldwide', 'eligibleLocations',
    'timezonePreference', 'eligibleTimezones', 'excludedLocations', 'international',
    'citizenship', 'visaSupport', 'relocationPaid', 'visaCriteria', 'relocationCriteria',
  ];

  // Changing the work model on the wizard starts the new branch blank; the edit form keeps
  // what was answered.
  static values = { resetDependents: Boolean };

  connect() {
    this.apply();
  }

  workModelChanged() {
    if (this.resetDependentsValue) this.resetDependents();
    this.apply();
  }

  apply() {
    const workModel = this.answerIn(this.workModelTarget);
    const office = workModel === 'onsite' || workModel === 'hybrid';
    const remote = workModel === 'remote';
    const worldwide = remote ? this.answerIn(this.worldwideTarget) : null;
    const timezonePreference = worldwide === 'true' ? this.answerIn(this.timezonePreferenceTarget) : null;

    this.show(this.officeTarget, office);
    this.show(this.attendanceTarget, workModel === 'hybrid');
    this.show(this.worldwideTarget, remote);
    this.show(this.eligibleLocationsTarget, office || worldwide === 'false');
    this.show(this.timezonePreferenceTarget, worldwide === 'true');
    this.show(this.eligibleTimezonesTarget, timezonePreference === 'true');
    this.show(this.excludedLocationsTarget, timezonePreference !== null);
    // A remote hire works from where they already live, so a work permit, visa help and
    // relocation only arise where there is an office.
    this.show(this.internationalTarget, office);
    this.applyInternational(office);
  }

  applyInternational(visible) {
    const citizenship = this.citizenshipTarget;
    const visaSupport = this.visaSupportTarget;

    // Requiring a work permit and offering visa help contradict each other, so ticking one
    // locks the other.
    citizenship.disabled = visaSupport.checked && !citizenship.checked;
    visaSupport.disabled = citizenship.checked && !visaSupport.checked;

    this.show(this.visaCriteriaTarget, visible && visaSupport.checked);
    this.show(this.relocationCriteriaTarget, visible && this.relocationPaidTarget.checked);
  }

  // The kit fields under the work model clear themselves on the reset event; the plain
  // inputs are cleared here, with the events a user's edit would fire so the autosave hears it.
  resetDependents() {
    if (this.hasAttendanceCountTarget) this.clear(this.attendanceCountTarget);
    [this.citizenshipTarget, this.visaSupportTarget, this.relocationPaidTarget].forEach(box => {
      box.checked = false;
      box.disabled = false;
      box.dispatchEvent(new Event('change', { bubbles: true }));
    });
    [this.visaCriteriaTarget, this.relocationCriteriaTarget].forEach(block => {
      block.querySelectorAll('textarea').forEach(field => this.clear(field));
    });

    this.dispatch('reset', { target: document });
  }

  clear(field) {
    field.value = '';
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  show(block, visible) {
    block.hidden = !visible;
  }

  // Null while nothing is chosen: the blank option, or no radio checked.
  answerIn(block) {
    const select = block.querySelector('select');
    if (select) return select.value || null;

    const radio = block.querySelector('input[type="radio"]:checked');
    return radio ? radio.value : null;
  }
}
